/**
 * `menuServiceEntries` — the static service-nav data the
 * `MenuSheet` renders (017/US1).
 *
 * Extracted from `MenuSheet.tsx` to keep the component file under the
 * 400-line architecture ceiling (`file-size-400.architecture.test.ts`,
 * FR-045) and to give the data model + types a single-purpose home
 * that downstream tests, the verifier, and `specs/017-menu-content-
 * alignment/data-model.md` can each point at directly.
 *
 * What lives here:
 *
 *   - `MenuShortcutChild` — the type for one submenu row. Post-026/US2
 *     this is a single `child-link` shape: every submenu child is an
 *     active navigating row (the deferred `child-deferred` variant was
 *     retired with the Valmennukset row the prototype dropped — see
 *     spec FR-006).
 *   - `MenuShortcut` — the discriminated union for one top-level
 *     service-nav row (`flat-link` | `expandable` | `external-link`).
 *   - `SERVICE_ENTRIES` — the ordered entries the menu renders.
 *
 * What does NOT live here: any rendering or runtime state. The
 * `openSubmenus: ReadonlySet<ExpandableKey>` and the toggle logic stay in
 * `MenuSheet.tsx` because they are component-local state, not static
 * data. Future evolution of the menu's content (proto re-orderings,
 * new destinations) should edit THIS file, not the component.
 *
 * Source of truth: `specs/017-menu-content-alignment/data-model.md`
 * §"MenuShortcut" and §"MenuShortcutChild". The concrete instances
 * below mirror that document's "Concrete instances" lists verbatim
 * (in order, with matching keys, labelKeys, and destinations).
 */

import type { ComponentType } from 'react';

import type { MessageKeys } from '../i18n/messages/index.js';

import {
  ChatBubbleIcon,
  DocumentIcon,
  GiftIcon,
  type IconProps,
  LockIcon,
  ScaleIcon,
  ShieldIcon,
} from './icons.js';

/**
 * Closed literal union of `expandable` parent keys (017/US1). The
 * `MenuShortcut` discriminated union's `expandable` variant carries
 * `key: ExpandableKey` rather than the looser `string`, so the
 * downstream `Record<ExpandableKey, string>` lookup in `MenuSheet.tsx`
 * (the `useId()`-allocated submenu-region id map) demands a matching
 * `useId()` allocation per key. Adding a new expandable entry requires
 * editing THIS union, the `SERVICE_ENTRIES` array literal, AND the
 * `submenuIdsByKey` record — three coordinated forces that fail
 * compile in concert if any one is omitted (TS10 — narrowing a closed
 * union; the fence is the point).
 */
export type ExpandableKey = 'lakikaveri' | 'palvelutEdut';

/**
 * Canonical ytkpalvelut.fi legal pages (026/US2, spec FR-007). These
 * are the two `external-link` destinations the menu opens in a new tab.
 * They migrated here when the legal links moved off the front page and
 * into the menu (FR-020).
 */
const TERMS_OF_USE_URL = 'https://www.ytkpalvelut.fi/sivuston-kayttoehdot/';
const PRIVACY_POLICY_URL = 'https://www.ytkpalvelut.fi/tietosuojaseloste/';

/**
 * One row inside an `expandable` parent's submenu.
 *
 *   - `child-link` → navigating `<NavLink>` row. Activating closes the
 *     drawer (B-INV-006).
 *
 * Post-026/US2 this is the only child shape: the prototype routes every
 * submenu child, so the deferred (`child-deferred`) variant that carried
 * the Tulossa badge for Valmennukset is gone (spec FR-006). The `kind`
 * discriminator is retained on the single remaining shape so a future
 * deferred/external child variant re-widens the union without reshaping
 * every existing entry. `MenuSheet` renders the single `child-link` shape
 * directly, so there is no child `kind` dispatch to guard today.
 */
export type MenuShortcutChild = {
  readonly kind: 'child-link';
  readonly key: string;
  readonly labelKey: MessageKeys;
  readonly to: string;
};

/**
 * One top-level service-nav row. Three subtypes:
 *
 *   - `flat-link` → real `<NavLink>`. Renders the icon + label only;
 *     no trailing chevron (proto-parity — the chevron reads as
 *     "this expands", which is true only for `expandable` parents).
 *   - `expandable` → WAI Disclosure parent. Click toggles a
 *     `role="group"` submenu region holding the `children` array.
 *     Carries a trailing chevron-right when collapsed and chevron-down
 *     when expanded as the disclosure-state affordance.
 *   - `external-link` → a real `<a href target="_blank" rel="noopener
 *     noreferrer">` to an off-app destination (026/US2, spec FR-007).
 *     Used for the two legal pages ("Sivuston käyttöehdot",
 *     "Tietosuojaseloste") that live on `ytkpalvelut.fi`, not in the
 *     SPA. Carries a leading `Icon` matching the prototype's legal
 *     glyphs (FileText for terms, Lock for privacy) and no chevron —
 *     `no-raw-anchor-internal` permits
 *     a raw `<a>` here because the destination is an absolute external
 *     URL, never an internal route (those MUST use `<NavLink>`).
 *   - `action` → a `<button>` row that triggers an in-app action instead
 *     of navigating (027 US6 / D-120: the "Ota yhteyttä" row opens the
 *     compose wizard modal; the legacy `/contact` destination is
 *     superseded). `MenuSheet` dispatches on `actionId`, closing the
 *     drawer first so the modal opens over the page, not the menu.
 */
export type MenuActionId = 'open-compose-wizard';

export type MenuShortcut =
  | {
      readonly kind: 'flat-link';
      readonly key: string;
      readonly labelKey: MessageKeys;
      readonly Icon: ComponentType<IconProps>;
      readonly to: string;
    }
  | {
      readonly kind: 'action';
      readonly key: string;
      readonly labelKey: MessageKeys;
      readonly Icon: ComponentType<IconProps>;
      readonly actionId: MenuActionId;
    }
  | {
      readonly kind: 'expandable';
      readonly key: ExpandableKey;
      readonly labelKey: MessageKeys;
      readonly Icon: ComponentType<IconProps>;
      readonly children: readonly MenuShortcutChild[];
    }
  | {
      readonly kind: 'external-link';
      readonly key: string;
      readonly labelKey: MessageKeys;
      readonly Icon: ComponentType<IconProps>;
      readonly href: string;
    };

/**
 * Service-nav rows, in render order. Mirror of the prototype's
 * `AppSidebar` order, re-aligned by 026/US2 (spec FR-005/FR-006/FR-007):
 * Lakikaveri → Jäsenyys ja edut → Tapaturmavakuutus → Ota yhteyttä →
 * the two legal external links (Sivuston käyttöehdot, Tietosuojaseloste).
 *
 * The two service parents are `expandable` disclosures with their
 * children arrays inline:
 *
 *   - Lakikaveri → 2 children: Tutustu Lakikaveriin (`/lakikaveri-info`),
 *     Lakiruutu (`/lakiruutu`). The Oikeusturvavakuutus child is removed
 *     with the article and route it pointed at.
 *   - Jäsenyys ja edut → 2 children: Verkkokoulutuspalvelu
 *     (`/eduhousen-verkkokoulutus`), Edut ja alennukset (`/jasenedut`).
 *     The Webinaaritallenteet child and the Valmennukset deferred row
 *     are removed (FR-006 — the prototype routes neither).
 *
 * Both `flat-link`s — Tapaturmavakuutus and Ota yhteyttä — render as
 * icon + label only; no trailing chevron (proto-parity — chevrons
 * signal "this expands" and the flat-link rows do not expand). The two
 * trailing `external-link` rows open the canonical ytkpalvelut.fi legal
 * pages in a new tab (FR-007); they moved here out of the front-page
 * footer (FR-020).
 */
export const SERVICE_ENTRIES: readonly MenuShortcut[] = [
  {
    kind: 'expandable',
    key: 'lakikaveri',
    labelKey: 'shell.menu.lakikaveri.label',
    Icon: ScaleIcon,
    children: [
      {
        kind: 'child-link',
        key: 'tutustuLakikaveriin',
        labelKey: 'shell.menu.lakikaveri.children.tutustuLakikaveriin',
        to: '/lakikaveri-info',
      },
      {
        kind: 'child-link',
        key: 'oikeusturvavakuutus',
        labelKey: 'shell.menu.lakikaveri.children.oikeusturvavakuutus',
        to: '/oikeusturvavakuutus',
      },
      {
        kind: 'child-link',
        key: 'lakiruutu',
        labelKey: 'shell.menu.lakikaveri.children.lakiruutu',
        to: '/lakiruutu',
      },
    ],
  },
  {
    kind: 'expandable',
    key: 'palvelutEdut',
    labelKey: 'shell.menu.palvelutEdut.label',
    Icon: GiftIcon,
    children: [
      {
        kind: 'child-link',
        key: 'verkkokoulutuspalvelu',
        labelKey: 'shell.menu.palvelutEdut.children.verkkokoulutuspalvelu',
        to: '/eduhousen-verkkokoulutus',
      },
      {
        kind: 'child-link',
        key: 'edutAlennukset',
        labelKey: 'shell.menu.palvelutEdut.children.edutAlennukset',
        to: '/jasenedut',
      },
    ],
  },
  {
    kind: 'flat-link',
    key: 'tapaturmavakuutus',
    to: '/vapaa-ajan-tapaturmavakuutus',
    labelKey: 'shell.menu.tapaturmavakuutus.label',
    Icon: ShieldIcon,
  },
  {
    // 027 US6 / D-120 single-experience: opens the compose wizard modal
    // (was a flat-link to the legacy /contact journey).
    kind: 'action',
    key: 'otaYhteytta',
    actionId: 'open-compose-wizard',
    labelKey: 'shell.menu.otaYhteytta.label',
    Icon: ChatBubbleIcon,
  },
  {
    kind: 'external-link',
    key: 'termsOfUse',
    labelKey: 'shell.menu.legal.termsOfUse',
    Icon: DocumentIcon,
    href: TERMS_OF_USE_URL,
  },
  {
    kind: 'external-link',
    key: 'privacyPolicy',
    labelKey: 'shell.menu.legal.privacyPolicy',
    Icon: LockIcon,
    href: PRIVACY_POLICY_URL,
  },
];
